import { createWorker } from 'tesseract.js';
import type { OCRProvider } from '../../core/reading/ocrProvider';

const MAX_IMAGE_DIMENSION = 1600;

export interface OCRImageDimensions {
  width: number;
  height: number;
}

export function calculateOCRImageDimensions(
  width: number,
  height: number,
  maxDimension = MAX_IMAGE_DIMENSION,
): OCRImageDimensions {
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export function normalizeOCRText(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

async function prepareImageForOCR(imageUrl: string): Promise<string> {
  if (typeof window === 'undefined' || typeof Image === 'undefined') return imageUrl;

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error('Gambar tidak dapat diproses'));
    element.src = imageUrl;
  });

  let source: CanvasImageSource = image;
  let bitmap: ImageBitmap | null = null;
  if (typeof createImageBitmap === 'function') {
    try {
      bitmap = await createImageBitmap(image, { imageOrientation: 'from-image' });
      source = bitmap;
    } catch {
      // ImageBitmap is an optimization; the decoded image remains the fallback.
    }
  }

  const sourceWidth = bitmap?.width ?? image.naturalWidth;
  const sourceHeight = bitmap?.height ?? image.naturalHeight;
  const dimensions = calculateOCRImageDimensions(sourceWidth, sourceHeight);
  if (!bitmap && dimensions.width === sourceWidth && dimensions.height === sourceHeight) {
    return imageUrl;
  }

  const canvas = document.createElement('canvas');
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap?.close();
    return imageUrl;
  }
  context.filter = 'grayscale(1) contrast(1.12)';
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  bitmap?.close();
  return canvas.toDataURL('image/jpeg', 0.88);
}

export const tesseractOCRProvider: OCRProvider = {
  async recognize(imageUrl, onProgress) {
    const preparedImage = await prepareImageForOCR(imageUrl);
    const worker = await createWorker('ind', 1, {
      logger: message => {
        if (message.status === 'recognizing text') {
          onProgress?.({
            progress: Math.round(message.progress * 100),
            status: message.status,
          });
        }
      },
    });

    try {
      const result = await worker.recognize(preparedImage);
      return { text: normalizeOCRText(result.data.text) };
    } finally {
      await worker.terminate();
    }
  },
};
